"use client";

import React from "react";
import Link from "next/link";

const ServicesAgreementServiceSpecific: React.FC = () => {
  return (
    <div className="terms py-3 py-md-4 py-xl-5" id="service_specific_terms">
      <section className="container">
        <h2 className="mb-3 mb-xl-5 text-center">
          Services Agreement <span className="d-block text-primary">Service-Specific Terms</span>
        </h2>
        <div className="row justify-content-center">
          {/* Table of contents */}
          <div className="col-md-10 col-lg-8 col-xxl-6 mb-5 mb-md-0">
            <ol className="list-unstyled terms-conditions-toc">
              <li className="bg-white rounded-3 p-2 shadow mb-3">
                <h3 className="lh-base lead fw-bold p-2 mb-0">
                  <Link href="#standard_terms">
                    <i className="fas fa-chevron-up me-1" />
                    Standard Terms
                  </Link>
                </h3>
              </li>
              <li className="bg-white rounded-3 p-2 shadow mb-0 terms-conditions-toc-links">
                <h3 className="lh-base lead fw-bold p-2 mb-0">
                  <i className="fas fa-chevron-right me-1" />
                  Service-Specific Terms
                </h3>
                <div className="p-2 pt-0">
                  <ol className="py-1">
                    <li><Link className="fw-bold text-primary" href="#a_defense_com_package">A Defense.com
                      Package</Link></li>
                    <li><Link className="fw-bold text-primary" href="#consultancy_services">Consultancy Services</Link>
                    </li>
                    <li><Link className="fw-bold text-primary" href="#cyber_essentials">Cyber Essentials</Link></li>
                    <li><Link className="fw-bold text-primary" href="#incident_response">Incident Response</Link></li>
                    <li><Link className="fw-bold text-primary" href="#managed_detection_and_amp_response">Managed
                      Detection & Response</Link></li>
                    <li><Link className="fw-bold text-primary" href="#outsourced_data_protection_officer_dpo">Outsourced
                      Data Protection Officer (DPO)</Link></li>
                    <li><Link className="fw-bold text-primary" href="#penetration_testing">Penetration Testing</Link>
                    </li>
                    <li><Link className="fw-bold text-primary" href="#virtual_chief_information_security_officer_vciso">Virtual
                      Chief Information Security Officer (VCISO)</Link></li>
                  </ol>
                </div>
              </li>
            </ol>
          </div>
          {/* Terms */}
          <div className="col-md-14 col-lg-16 col-xxl-18 main terms-conditions-toc-content">
            <div className="mb-4">
              <p>(incorporated into the Services Agreement Order Form and incorporating the Services Agreement Standard
                Terms, any Annex and any Order Form Services Addendum, all together the &quot;Services Agreement&quot;)</p>
              <p className="fw-bold">Alphabetical Order</p>
              <p>Supplier will provide Customer the following Service(s) as set forth on the Order Form:</p>
              <ol className="decimal-list">
                <li id="a_defense_com_package">
                  <h3 className="h6 fw-bold text-uppercase">A Defense.com Package</h3>
                  <p>Defense.com is Supplier&rsquo;s SaaS security management solution, the Defense.com Platform, that
                    integrates into any size organisation, providing Customer access to a scalable suite of cyber
                    security tools, each playing a part in protecting Customer&rsquo;s business from new and existing
                    threats and, as a whole, providing Customer&rsquo;s Defense.com Users a Defense.com Licence to
                    access to a 360&deg; view of Customer&rsquo;s security profile 24 hours a day.</p>
                  <ol type="A">
                    <li>
                      <h4 className="lh-base lead fw-bold mb-2 text-uppercase">Asset Profile</h4>
                      <p>Supplier will provide access to Customer to define its digital assets profile which assists in
                        defining the attack surface and allows Supplier to automatically align threats to
                        Customer&rsquo;s unique attack surface. Customer will add assets to ensure the correct threat
                        intelligence feeds align to Customer&lsquo;s environment.</p>
                    </li>
                    <li>
                      <h4 className="lh-base lead fw-bold mb-2 text-uppercase">Breach Monitoring</h4>
                      <p>Supplier will provide an automated monitoring solution performing surface web, deep web and
                        dark web scans 24 hours a day for Customer&rsquo;s designated business data which includes
                        Supplier&rsquo;s comprehensive source feeds -- IRC chatrooms, bin sites, data dumps, social
                        sources and dark web sites, to detect sensitive data efficiently.</p>
                    </li>
                    <li>
                      <h4 className="lh-base lead fw-bold mb-2 text-uppercase">Cyber Health Check</h4>
                      <p>Supplier will provide access to an online self-assessment tool that enables Customer to assess
                        its current cyber security and information security posture by answering a series of questions
                        based on modules covering a range of best practice cyber/information security controls,
                        following completion of which Customer will receive an online report of its current status using
                        a RAG (red, amber, green) indication which can be downloaded, and any threats identified will be
                        automatically fed into Customer&rsquo;s Defense.com Threat Dashboard. For each successfully
                        passed question module, Customer may download a pass certificate. Customer may choose which
                        modules to take and can re-take any of the assessment modules at any time.</p>
                    </li>
                    <li>
                      <h4 className="lh-base lead fw-bold mb-2 text-uppercase">Endpoint Protection</h4>
                      <p>Supplier will provide Endpoint Protection software and the SaaS platform to manage the
                        endpoints. Supplier will also provide staff to manage, tune and support the platform. The
                        following are included:</p>
                      <p><strong>Windows</strong> &ndash; FileScan; ContentControl; UserControl; Application
                        Backlisting; DataProtection; TrafficScan; AntiPhishing Firewall; BehavioralScan; MailServers
                        (Exchange - only servers); DeviceControl; AntiExploit.</p>
                      <p><strong>Mac</strong> &ndash; FileScan; Update Server; and Content Control with TrafficScan +
                        Antiphishing</p>
                      <p><strong>Linux</strong> &ndash; FileScan; and Update Server</p>
                      <p>Customer will install the software to secure endpoints and/or entry points on Customer&rsquo;s
                        end-user devices to prevent file-based malware and detect and block malicious activity through
                        automated vulnerability scanning.</p>
                    </li>
                    <li>
                      <h4 className="lh-base lead fw-bold mb-2 text-uppercase">Microsoft Office 365 Monitoring</h4>
                      <p>Supplier will provide access to the automated Microsoft 365 Monitoring feature which will
                        automatically create threats, escalating the highest risks to Customer and provide remediation
                        advice. Customer will provide support and necessary secure access to Customer&rsquo;s MS Office
                        365 account with privileges for the Supplier to ingest data and alert on threats
                        identified. </p>
                    </li>
                    <li>
                      <h4 className="lh-base lead fw-bold mb-2 text-uppercase">Panic Button</h4>
                      <p>Supplier will provide a 24x7x365 emergency help button which allows Customer to raise potential
                        security incidents with our trained, experienced team. Supplier provides fast-tracked
                        preliminary incident response advice for all types of security events and cyber incidents
                        including, but not limited to, suspected data breaches, ransomware attacks, insider threat,
                        suspicious network activity and known vulnerability exposure. This service is intended to triage
                        potential security incidents and provide practical advice for resolution, but does not include
                        any remediation work from the Supplier.</p>
                    </li>
                    <li>
                      <h4 className="lh-base lead fw-bold mb-2 text-uppercase">Phishing Simulator</h4>
                      <p>Supplier will provide access to the Phishing Simulator feature, which enables Customer to send
                        safe phishing emails to test Customer staff&rsquo;s vigilance and identify any weaknesses in
                        their security knowledge. Customer will use the platform to schedule and select the appropriate
                        campaign per team, track results and take remediation steps following the outcome of the test.
                        Customer will setup whitelisting of Supplier IP addresses and email domains as defined in
                        Supplier provided help guides</p>
                    </li>
                    <li>
                      <h4 className="lh-base lead fw-bold mb-2 text-uppercase">Security Information and Event Management
                        (SIEM)</h4>
                      <p>Supplier will provide a SaaS based centralised log management to aggregate all log data in a
                        single location and into a common format. Supplier will store log data for 12 months in an
                        archive and provide 90 days of logs for immediate searching. Customer will install with support
                        of Supplier the relevant software and virtual hardware to support the delivery of the
                        service.</p>
                    </li>
                    <li>
                      <h4 className="lh-base lead fw-bold mb-2 text-uppercase">Security Support</h4>
                      <p>Provision of a Supplier helpline, including audio and/or messaging, that offers first level
                        response, general guidance and assistance to Customer, within 24 hours of a logged service
                        request (excluding where the response due time falls within a weekend or national holiday), for
                        cyber security questions Supplier deems to be common and frequently asked.</p>
                    </li>
                    <li>
                      <h4 className="lh-base lead fw-bold mb-2 text-uppercase">Threat Dashboard</h4>
                      <p>Supplier will provide functionality in a single interface that displays threats across all the
                        features provided in Defense.com. Threats are automatically populated by each feature, such as
                        live threat intelligence tailored to Customer. Once threats have been populated, Defense.com
                        provides powerful features to allow Customer to manage each threat and allocate threats to
                        specific individuals for remediation. The platform will assign risk levels and allow the
                        businesses to drill down into specific threat information and understand the business impact.
                        Customer will action threats and perform remediations as identified by the platform or will take
                        action to accept risk or acknowledge threats as false positives.</p>
                    </li>
                    <li>
                      <h4 className="lh-base lead fw-bold mb-2 text-uppercase">Threat Intelligence</h4>
                      <p>Supplier will provide Customer a customised list of cyber threats, continually updated by
                        experts based on the latest intelligence from commercial, opensource and custom-built feeds.
                        Customer will define assets to ensure the correct intelligence is supplied relevant to
                        Customer&rsquo;s environment.</p>
                    </li>
                    <li>
                      <h4 className="lh-base lead fw-bold mb-2 text-uppercase">Threat Recon</h4>
                      <p>Supplier will provide access to Threat Recon which presents the attack surface of
                        Customer&rsquo;s business to highlight risks. Threat Recon will automatically perform predefined
                        tests that are used by attackers to test the exposure of the business. These checks include
                        sub-domain detection, port scanning of top 20 ports, network information gathering, SSL
                        validation, potential risk based on site popularity, email spoofing protection checks, block
                        list lookup, security best practices assessment and other checks as offered by Supplier.
                        Customer will provide all relevant internet facing web domains as the scope for the checks.</p>
                    </li>
                    <li>
                      <h4 className="lh-base lead fw-bold mb-2 text-uppercase">Training Videos & Exams</h4>
                      <p>Supplier will provide a range of standard training courses covering varied cyber security,
                        information security and compliance topics. These are delivered through Defense.com with a range
                        of videos and associated exams which, along with built in reporting, allow Customer to track
                        adoption.</p>
                    </li>
                    <li>
                      <h4 className="lh-base lead fw-bold mb-2 text-uppercase">Vulnerability Scanning</h4>
                      <p>Supplier will provide a platform to allow Customer to run automated Vulnerability Scans of the
                        most common ports with the option to customise to Customer&rsquo;s requirements, to assess
                        systems or applications for known security flaws and weaknesses. Supplier will provide threats
                        that can be managed, allocated, assigned and risks accepted via Defense.com in addition to
                        actionable remediation advice. The service will allow Customer to identify assets that are prone
                        to attacks. Customer will define the scope of the automated scans and take measures to patch or
                        remediate the threats as provided by Supplier&rsquo;s automated process.</p>
                    </li>
                  </ol>
                </li>
                <li id="consultancy_services">
                  <h3 className="lh-base lead fw-bold mb-2 text-uppercase">Consultancy Services</h3>
                  <p>Supplier will remotely provide Customer advice and support covering information security topics,
                    including, without limitation, frameworks such as ISO 27001, NIST, CIS, ISO22301 and General Data
                    Protection Regulation (GDPR) data protection. Where specified, Supplier will assist Customer to work
                    toward improvement of its business performance in terms of operations, management, structure and/or
                    strategy regarding cyber security and/or GDPR compliance. On-site visits may be arranged with
                    Customer in exceptional circumstances.</p>
                  <ol type="A">
                    <li>
                      <h4 className="lh-base lead fw-bold mb-2 text-uppercase">Cyber Security Assessment</h4>
                      <p>Supplier will provide an experienced Information Security Consultant to assess the current
                        level of information/cyber security in Customer&rsquo;s organisation. This will be based on the
                        NIST CSF and ISO 27001/27002 controls and the output will be a report detailing the level of
                        compliance against each of the requirements along with recommendations on how to achieve
                        compliance.</p>
                    </li>
                    <li>
                      <h4 className="lh-base lead fw-bold mb-2 text-uppercase">Data Privacy Advisor (DPA)</h4>
                      <p>Supplier will provide Customer access to up to 2 hours per month of remote support for queries
                        and questions relating to GDPR and data privacy matters. Customers can contact the DPA service
                        via a centralised mailbox initially and then queries can be dealt with via email, phone or video
                        conferencing.</p>
                    </li>
                    <li>
                      <h4 className="lh-base lead fw-bold mb-2 text-uppercase">GDPR Audit</h4>
                      <p>Supplier will provide an experienced GDPR consultant to audit the current level of compliance
                        to GDPR. The output of the audit will be a report that will outline any non-conformities. During
                        the audit, which will be conducted remotely, Customer will need to provide access to key staff,
                        documentation and evidence to support the audit.</p>
                    </li>
                    <li>
                      <h4 className="lh-base lead fw-bold mb-2 text-uppercase">GDPR Gap Analysis</h4>
                      <p>Supplier will provide an experienced GDPR consultant to undertake a gap analysis against the
                        requirements of GDPR. The output of the gap analysis will be a report detailing the current
                        level of compliance to each of the requirements along with a document review (which will include
                        a maximum of 20 GDPR related policies, procedures or documents) with recommendations and an
                        action plan outlining what needs to be done to achieve compliance. During the gap analysis,
                        which will be conducted via a series of online interviews with key stakeholders, Customer will
                        be required to provide documents, e.g., policies and procedures that are currently in place for
                        assessment.</p>
                    </li>
                    <li>
                      <h4 className="lh-base lead fw-bold mb-2 text-uppercase">GDPR Implementation</h4>
                      <p>Supplier will provide an experienced GDPR consultant to deliver the GDPR implementation
                        project. The service, which will be delivered remotely, will include preparation of all required
                        documentation along with advice and support on how to ensure current processes are compliant.
                        Customer will be required to play an active part in the implementation through interviews and
                        workshops.</p>
                    </li>
                    <li>
                      <h4 className="lh-base lead fw-bold mb-2 text-uppercase">ISO 27001 Gap Analysis</h4>
                      <p>Supplier will provide an experienced ISO 27001 consultant to undertake a Gap Analysis against,
                        as appropriate, the version of the ISO 27001 standard ISO requested by Customer in accordance
                        with the agreed scope. The output of the gap analysis will be a report detailing the current
                        level of compliance to each of the requirements of ISO 27001 with recommendations on what needs
                        to be done to achieve compliance. During the Gap Analysis, which will be conducted via a series
                        of online interviews with key stakeholders, Customer will be required to provide documents,
                        e.g., policies and procedures that are currently in place for assessment.</p>
                    </li>
                    <li>
                      <h4 className="lh-base lead fw-bold mb-2 text-uppercase">ISO 27001 Implementation</h4>
                      <p>Supplier will provide an experienced ISO 27001 lead implementer to deliver an ISO 27001
                        implementation project to enable Customer&rsquo;s readiness for certification by an external
                        UKAS accredited certification body. The implementation service, which will be delivered
                        remotely, will include training of all staff on the Information Security Management System the
                        consultant is implementing and preparation of all required documentation. Customer will be
                        required to play an active part in the implementation through interviews and workshops.</p>
                    </li>
                    <li>
                      <h4 className="lh-base lead fw-bold mb-2 text-uppercase">ISO 27001 Internal Audit</h4>
                      <p>Supplier will provide an experienced ISO 27001 auditor to conduct an internal audit against the
                        agreed requirements and scope of the Information Security Management System. The output of the
                        internal audit will be a report, written in accordance with the requirements of the ISO 27001
                        standard that will outline any non-conformities and opportunities for improvement. During the
                        audit, which will be conducted remotely, Customer will need to provide access to key staff,
                        documentation and evidence to support the audit.</p>
                    </li>
                    <li>
                      <h4 className="lh-base lead fw-bold mb-2 text-uppercase">Managed Phishing Campaigns</h4>
                      <p>Supplier will perform tailored Phishing simulations (campaigns) to test Customer staff&rsquo;s
                        vigilance and identify any weaknesses in their security knowledge. Supplier will provide a
                        report documenting the results of the Phishing Campaigns through a secure portal. Customer will
                        work closely with Suppliers to agree the scope, requirements of the test, schedule, track
                        results and take remediation steps following the outcome of the test. Customer will provide
                        target employee details including, e.g., their email address, role and full name.</p>
                    </li>
                    <li>
                      <h4 className="lh-base lead fw-bold mb-2 text-uppercase">Payment Card Industry Data Security Standard
                        (PCI DSS) Consultancy</h4>
                      <p>Supplier will provide an experienced information security consultant to provide a range of PCI
                        DSS consultancy services to ensure Customer has implemented all the necessary policies,
                        procedures and technical controls to achieve PCI DSS certification. Where available, Customer
                        will be required to provide an asset inventory for systems in scope for PCI along with a network
                        diagram and data flow diagram along with any other relevant supporting policies, procedures and
                        documentation.</p>
                    </li>
                    <li>
                      <h4 className="lh-base lead fw-bold mb-2 text-uppercase">Service Organisation Control (SOC) 2</h4>
                      <p>Supplier will provide an experienced information security consultant to provide a range of SOC2
                        consultancy services to assist Customer in the implementation of all necessary policies,
                        procedures and technical controls in preparation for an audit by a Certified Public Accountant
                        (CPA).</p>
                    </li>
                    <li>
                      <h4 className="lh-base lead fw-bold mb-2 text-uppercase">Training</h4>
                      <p>Supplier will provide a range of standard training courses covering both cyber security
                        awareness and GDPR awareness. These can be delivered through Defense.com with a range of videos
                        and associated exams which, along with built in reporting, allows Customer to track that staff
                        have watched the videos and completed their exams. Other delivery methods include on-site
                        training and virtual training using video conferencing tools. Bespoke training courses covering
                        specific cybersecurity or GDPR topics can also be developed and delivered for Customers in any
                        format, be that video, online training or, where agreed, physically on site. Supplier will
                        provide a copy of any training materials to Customer in pdf format upon completion of the
                        training.</p>
                    </li>
                  </ol>
                </li>
                <li id="cyber_essentials">
                  <h3 className="lh-base lead fw-bold mb-2 text-uppercase">Cyber Essentials</h3>
                  <p>Supplier will assist Customer to achieve certification under the NCSC Cyber Essentials scheme.
                    Support is provided in line with the level of service Customer has contracted for as per the
                    following:</p>
                  <div className="table-responsive">
                    <table className="table table-hover mb-2">
                      <thead>
                      <tr>
                        <th>Feature</th>
                        <th>Essentials</th>
                        <th>Essentials Premium</th>
                        <th>Essentials Plus</th>
                        <th>Plus Premium</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr>
                        <td>Cyber Essentials certification</td>
                        <td>Included</td>
                        <td>Included</td>
                        <td>Included</td>
                        <td>Included</td>
                      </tr>
                      <tr>
                        <td>Cyber Essentials Plus certification</td>
                        <td>&nbsp;</td>
                        <td>&nbsp;</td>
                        <td>Included</td>
                        <td>Included</td>
                      </tr>
                      <tr>
                        <td>Up to 25k FREE cyber insurance*</td>
                        <td>Included</td>
                        <td>Included</td>
                        <td>Included</td>
                        <td>Included</td>
                      </tr>
                      <tr>
                        <td>Free additional cyber protection tools (i)</td>
                        <td>Included</td>
                        <td>Included</td>
                        <td>Included</td>
                        <td>Included</td>
                      </tr>
                      <tr>
                        <td>Tailored policy documents</td>
                        <td>&nbsp;</td>
                        <td>Included</td>
                        <td>&nbsp;</td>
                        <td>Included</td>
                      </tr>
                      <tr>
                        <td>Remote support (ii)</td>
                        <td>2h included</td>
                        <td>4h included</td>
                        <td>4h included</td>
                        <td>6h included</td>
                      </tr>
                      <tr>
                        <td>Free retest</td>
                        <td>1 free retest</td>
                        <td>1 free retest</td>
                        <td>1 free retest per certificate</td>
                        <td>Up to 3 retests per certificate</td>
                      </tr>
                      </tbody>
                    </table>
                  </div>
                  <p>Supplier in addition will provide:</p>
                  <ol type="i">
                    <li>
                      Additional cyber protection tools as specified on the Order Form such as: vulnerability scanning,
                      endpoint protection, online training and exams and Asset Profile.
                    </li>
                    <li>
                      emote support via telephone, email or video conferencing. Additional support time required is
                      available at our standard rate.
                    </li>
                  </ol>
                  <p>*Cyber Insurance:</p>
                  <p>Free cyber insurance, provided by a third party insurer, is provided to UK companies as part of the
                    scheme if the basic certification covers the entire organisation.</p>
                  <p>Customer acknowledges that the Cyber Essentials scheme is intended to reflect that the certificated
                    organisation has established the cyber security profile set out in the Cyber Essentials scheme
                    documents only and that receipt of a scheme certificate does not indicate or certify that the
                    certificate holder is free from cyber security vulnerabilities. Customer acknowledges that Supplier
                    has not warranted or represented the Cyber Essentials scheme or certification under the Cyber
                    Essentials scheme as conferring any additional benefit to Customer.</p>
                  <ol type="A">
                    <li>
                      <h4 className="lh-base lead fw-bold mb-2 text-uppercase">CYBER ESSENTIALS (EXCLUDING CYBER ESSENTIALS
                        PLUS) </h4>
                      <p>After purchasing Cyber Essentials, Customer will be required to confirm via email when they are
                        ready to complete their assessment. The Cyber Essentials team will send an email after initial
                        purchase, asking to be informed when Customer is ready to proceed. Customer will not be given
                        access to complete their assessment until a response is received.</p>
                      <p>Customer shall complete and submit the self-assessment form within a month of being added to
                        the portal.</p>
                      <p>Customer shall comply with the Cyber Essentials scheme documentation and all reasonable
                        directions made to Customer by the Authority, a Cyber Essentials Partner or a certification
                        body.</p>
                      <p>Subject to Customer&rsquo;s completion of a Cyber essentials self-assessment
                        (the &ldquo;Questionnaire&rdquo;), Supplier will assess the Customer-completed Questionnaire
                        against the Cyber Essentials Scheme criteria.</p>
                      <p>The Questionnaire account will remain open and accessible for six (6) months. If Customer has
                        not submitted the Questionnaire within 6 months, the assessment will expire and no refund will
                        be permitted. If Customer wishes to complete the Questionnaire after expiration, it will be
                        required to order Cyber Essentials again.</p>
                      <p>If the completed Questionnaire assessment meets the Cyber Essentials scheme criteria (which
                        Supplier shall assess in accordance with the IASME marking scheme) Supplier will notify Customer
                        and, subject to Customer meeting its obligations, Supplier will arrange for the issue of a IASME
                        Certificate to Customer.</p>
                      <p>If a certification only service has been purchased by Customer, no support will be provided by
                        Supplier other than assistance gaining access to the Questionnaire.</p>
                      <p>If Customer has not submitted its application after a month of being added to the portal,
                        reminders will be sent to Customer as follows:</p>
                      <ul>
                        <li>After 4 weeks of inactivity &ndash; one reminder email will be sent to the main contact on
                          the application.
                        </li>
                        <li>After another 2 weeks a second reminder will be sent if Customer has still not submitted its
                          application.
                        </li>
                        <li>After another 2 weeks a third reminder will be sent if Customer has still not submitted its
                          application.
                        </li>
                        <li>After another 2 weeks a fourth and final reminder will be sent if Customer has still not
                          submitted its application.
                        </li>
                      </ul>
                      <p>If all the above reminders do not result in a reply with either an offered date or a
                        submission, the customer will be invoiced either at the point where their account expires (6
                        months after the questionnaire account being added) or when their contract ends, whichever is
                        sooner.</p>
                      <p>Where Customer&rsquo;s order has not been completed within 12 months from the date it was
                        placed, the assessment will be marked as a &lsquo;fail&rsquo; and Customer will be invoiced.</p>
                      <p>Cancellation of orders is not possible due to the systems and third parties involved in
                        providing the service. Therefore, incomplete applications will be marked as
                        a &lsquo;fail&rsquo; and Customer will be invoiced.</p>
                    </li>
                    <li>
                      <h4 className="lh-base lead fw-bold mb-2 text-uppercase">CYBER ESSENTIALS PLUS:</h4>
                      <p>Customer must achieve an additional cyber essential level within 90 days of certifying against
                        Cyber Essentials (excluding Plus). Any free retest offerings must be used within the 90-day
                        deadline for completing Cyber Essentials Plus.</p>
                      <p>If Customer is unable to pass within that time through no fault of Supplier, the application
                        will be marked as a &lsquo;fail&rsquo;.</p>
                      <p>Where Customer fails the Cyber Essentials Plus test, Customer will have 30 days to remediate
                        any issues found and get a retest (within the 90 days).</p>
                      <p>Where Customer refuses or fails to provide the access required to conduct the test, the test
                        will be marked as a &lsquo;fail&rsquo;.</p>
                      <p>The following charges will apply to any Customer short-term cancellation and rescheduling:</p>
                      <ol type="a">
                        <li>cancellation or rescheduling requested between 7 and 14 days before the scheduled start date
                          for delivery of any Services: 50% of the scheduled Service Fees of the cancelled or
                          rescheduled Service(s); or
                        </li>
                        <li>for cancellation or rescheduling requested within 7 days before the scheduled start date for
                          delivery of any Services: 100% of the scheduled Service Fees of the cancelled or rescheduled
                          Service(s).
                        </li>
                      </ol>
                    </li>
                  </ol>
                </li>
                <li id="incident_response">
                  <h3 className="lh-base lead fw-bold mb-2 text-uppercase">Incident Response</h3>
                  <p>Supplier will provide Customer assistance within three hours via Supplier&rsquo;s SOC hotline which
                    is available 24x7x365. The emergency request will consist of an initial assessment and triage via
                    phone to discover and confirm the nature and impact of the incident within Customer&lsquo;s environment,
                    including the collection and analysis of all relevant information, and to provide advice based on
                    the nature of the incident. Customer will provide all necessary resources and information to ensure
                    the success of the service. If more detailed analysis is required or the incident has been confirmed
                    as a data breach the service will provide additional support to investigate the extent of the
                    incident which may include forensic analysis supported onsite (Digital Forensics) where required at
                    an additional cost as defined in the Services Agreement Standard Terms. Digital Forensics support
                    will be charged, as required, at a day rate of ~&pound;1,500.00 as updated by Suppler from time to
                    time.</p>
                  <ol type="A">
                    <li>Customer shall provide and coordinate Supplier&rsquo;s access to the systems to be investigated.
                      Before any system access is granted, Customer shall inform Supplier in writing and in advance of
                      any security and access standards or requirements that may change.
                    </li>
                    <li>During an assessment, the configuration of Customer&rsquo;s network will be kept as stable as
                      possible (i.e., no new systems or configuration changes). If changes are required, Customer shall
                      inform Supplier, and a mutually acceptable testing schedule shall be agreed upon.
                    </li>
                    <li>
                      <p>During the initial notification call, Customer shall provide Supplier with information below to
                        create an incident ticket. Customer shall appoint an authorised contact person for every
                        incident raised. The appointed contact person shall be preregistered with Supplier.</p>
                      <p>Customer Name</p>
                      <ol type="i">
                        <li>Locations affected by the incident</li>
                        <li>Priority of the incident</li>
                        <li>Information on how the incident was identified</li>
                      </ol>
                      <p>Contact Name</p>
                      <ol type="i" start={4}>
                        <li>Contact Phone Number</li>
                        <li>Details of incident</li>
                        <li>Information on when the incident was first identified</li>
                      </ol>
                      <p><strong>Note:</strong> Should Customer consider the nature of the incident to preclude the
                        support desk being provided with these details, Customer contact may simply state that the
                        incident is a &lsquo;flash priority&rsquo; at which point Supplier support personnel will
                        request no further details and will immediately initiate the response procedures.</p>
                    </li>
                    <li>It is also the responsibility of Customer to provide details of the priority classification for
                      discussion prior to rollout of the services. Further to this, it is considered Customer&rsquo;s
                      responsibility to make the following information available and the processes followed. Supplier
                      will work closely with Customer (as a separate engagement) to ensure that all responsibilities can
                      be met.
                    </li>
                    <li>Customer shall maintain accurate network diagrams and make these diagrams available to Supplier
                      as required.
                    </li>
                    <li>Customer shall maintain accurate process maps and diagrams, detailing the systems involved with
                      the transmission, storage, or processing of sensitive information.
                    </li>
                    <li>Customer shall provide an updated list (per incident) of personnel with which the aspects of the
                      incident may be openly discussed. All other personnel will simply be directed toward their own
                      management for information.
                    </li>
                    <li>Customer shall provide contact information for senior personnel related to affected departments
                      or systems to be contacted for further information (see previous point).
                    </li>
                  </ol>
                </li>
                <li id="managed_detection_and_amp_response">
                  <h3 className="lh-base lead fw-bold mb-2 text-uppercase">Managed Detection &amp; Response</h3>
                  <p>Supplier will provide a SaaS based security information and event management platform to deliver
                    real-time analysis of potential cybersecurity threats. Supplier&rsquo;s security analysts will
                    analyse Customer logs 24x7x365 to identify security threats and raise events to Customer for
                    investigation. Customer will install, with the support of Supplier, relevant software and virtual
                    hardware to support the delivery of the Service.</p>
                  <ol type="A">
                    <li>
                      <h4 className="lh-base lead fw-bold mb-2 text-uppercase">Definitions</h4>
                      <p>The following additional definitions shall apply to this Service:</p>
                      <p>&ldquo;APT&rdquo; or &ldquo;Advance Persistent Threat&rdquo; means a set of stealthy and
                        continuous computer hacking processes.</p>
                      <p>&ldquo;Attack&rdquo; means the inflow of malicious or illegitimate call requests to an
                        infrastructure or web platform for malicious intent. The purpose of this is to gain access or to
                        deliver disruption to the infrastructure.</p>
                      <p>&ldquo;Critical&rdquo; means the classification by Supplier of a Security Event as defined in
                        the Managed Detection &amp; Response Service Level Agreement (MDR SLA) that will receive the
                        highest level of response from Supplier&lsquo;s designated trained security professionals.</p>
                      <p>&ldquo;Incident Response Plan&rdquo; means the overarching framework for both
                        parties&rsquo; efficient and professional reactions during a security incident.</p>
                      <p>&ldquo;Non-Critical&rdquo; means a Security Event as defined in the MDR SLA that does not
                        require immediate attention because it is deemed not to be critical.</p>
                      <p>&ldquo;Runbook&rdquo; means a routine compilation of procedures and operations which designated
                        employees will use as a reference.</p>
                      <p>&ldquo;Security Event&rdquo; means a change in the everyday operations of a network or
                        information technology service, which indicates that a security policy may have been violated or
                        a security safeguard may have failed.</p>
                      <p>&ldquo;Security Incident&rdquo; means a situation where an adverse impact has resulted from a
                        Security Event.</p>
                      <p>&ldquo;SIEM&rdquo; means software products and services combining security information
                        management (SIM) and security event management (SEM) that provide real-time analysis of security
                        alerts generated by network hardware and software applications.</p>
                      <p>&ldquo;Threat Investigation&rdquo; means any actions taken by Supplier to validate a Security
                        Event as a real threat and to rule out the possibility of it being a false alert.</p>
                      <p>&ldquo;Threat Signatures&rdquo; means any information provided by Vendors to help identify any
                        threats that could impact Customer&rsquo;s network or infrastructure.</p>
                      <p>&ldquo;Vendors&rdquo; means third parties who provide Supplier with infrastructure, products,
                        intelligence or expertise to allow us to provide the Services, including but not limited to
                        dedicated hardware appliances, Threat Signatures, and vulnerability scanning services.</p>
                      <p>&ldquo;Zero-day&rdquo; means an attack that exploits a previously unknown vulnerability in a
                        computer application or operating system, one that developers have not had time to address and
                        patch.</p>
                    </li>
                    <li>
                      <h4 className="lh-base lead fw-bold mb-2 text-uppercase">Supplier Obligations</h4>
                      <p>Supplier will provide the following in accordance with the Order Form, the MDR SLA and
                        Runbooks.</p>
                      <p>Active monitoring of all systems in scope for Security Event using a threat intelligence SIEM
                        module.</p>
                      <p>Correlate various logs to identify any Security Events that may carry a potential threat.</p>
                      <p>Interpretation of logs and audit trail and focus on threats that matter most to Customer.</p>
                      <p>Incident investigation from triggered alerts and abnormal behaviour in accordance with a
                        well-defined and agreed Runbook.</p>
                      <p>Customer notification and incident reporting in accordance with the agreed incident response
                        plan.</p>
                      <p>Provide recommendations for dealing with incidents.</p>
                      <p>Ongoing management and maintenance of the threat (SIEM) appliances: installation, migration and
                        configuration of the SIEM hardware or software.</p>
                      <p>All configuration files will be kept and backed-up for a minimum of 30 days with daily restore
                        points covering one week, unless an alternative period is formally requested by Customer and
                        agreed by Supplier.</p>
                      <p>All logs will be kept and backed-up for a minimum period of 30 days, with immediate access and
                        1 year in archive.</p>
                      <p>Incident reports will be generated within 24 hours following any critical Security Event as
                        soon as the investigation has been completed. Upon request, Supplier will provide incident
                        reports for any critical Security Events that have occurred.</p>
                      <p>Access to an online portal which will contain up-to-date incident reports and change control
                        information.</p>
                    </li>
                    <li>
                      <h4 className="lh-base lead fw-bold mb-2 text-uppercase">Customer Obligations</h4>
                      <p>Customer agrees to perform the obligations and that Supplier&rsquo;s ability to perform its
                        obligations and its liability are dependent on Customer&rsquo;s compliance with the
                        following:</p>
                      <p>Customer is required to make appropriate staff available to help Supplier with the following
                        items (if applicable):</p>
                      <ol type="i">
                        <li>Runbooks</li>
                        <li>Incident Response Plan</li>
                        <li>Any other documents or procedures required to provide the Services.</li>
                        <li>Any infrastructure or platform used to provide the Services</li>
                        <li>Any other procedures required to provide the Services</li>
                      </ol>
                      <p>In the case of a Security Event occurring, Customer agrees to work in line with agreed
                        Runbooks.</p>
                      <p>Customer agrees and understands that the effectiveness of the Services depends on the
                        collaboration during the on-boarding phase that will define and assess the processes, escalation
                        points and on-going communication channels.</p>
                      <p>Customer must inform Supplier of any changes that could affect any individual Runbook or the
                        Incident Response Plan. This also includes the escalation procedures, availability and contact
                        details of personnel, reliability, performance and any other security or compliance related
                        requirements.</p>
                    </li>
                    <li>
                      <h4 className="lh-base lead fw-bold mb-2 text-uppercase">Supplier MDR SLA</h4>
                      <p>Supplier will work in line with the agreed Runbooks.</p>
                      <p>Supplier will monitor all key components used in the delivery of the Services 24x7x365.</p>
                      <p>In the event of any issues arising, Supplier will work to identify and resolve any threats or
                        issues as quickly as possible.</p>
                      <p>Supplier will provide technical staff 24x7x365 to support the Services provided and to assist
                        Customer with any issues that may arise. A 24-hour telephone number will be available for
                        Customers. Email support will also be provided but should not be used for emergencies.</p>
                      <p>If a Critical event occurs, Supplier will perform an initial Threat Investigation and then
                        notify Customer within 30 minutes of the Security Event if it has been deemed by Supplier to
                        have become a Critical event.</p>
                      <p>If a Security Event occurs of a Non-Critical nature, Supplier will take actions in line with
                        the agreed Runbook.</p>
                      <p>If a Security Event occurs Supplier will first carry out a Threat Investigation and will then
                        respond to Customer within the timeframes listed in the table below.</p>
                      <p>For any Security Event which Supplier deems to be Critical prior to the Threat Investigation
                        being completed, Supplier will contact and regularly update Customer.</p>
                      <p>The Security Event severity is typically set via the stage at which the event comes in the
                        attack kill chain. The further along this process the more severe the event.</p>
                      <div className="table-responsive">
                        <table className="table table-hover mb-2">
                          <thead>
                          <tr>
                            <th>SEVERITY LEVEL</th>
                            <th>EXAMPLE</th>
                            <th>COMMUNICATION METHOD</th>
                            <th>RESPONSE TIME</th>
                          </tr>
                          </thead>
                          <tbody>
                          <tr>
                            <td>Critical</td>
                            <td>Command and Control communication established / outbound connection to known bad actor
                              address
                            </td>
                            <td>Phone, portal and email</td>
                            <td>30 minutes</td>
                          </tr>
                          <tr>
                            <td>High</td>
                            <td>Brute-force activity against externally facing systems with legitimate accounts</td>
                            <td>Phone, portal and email</td>
                            <td>30 minutes</td>
                          </tr>
                          <tr>
                            <td>Medium</td>
                            <td>Infrastructure or system version Information disclosure</td>
                            <td>Portal and email</td>
                            <td>-</td>
                          </tr>
                          <tr>
                            <td>Low</td>
                            <td>Administrator account lockout</td>
                            <td>Portal and email</td>
                            <td>-</td>
                          </tr>
                          <tr>
                            <td>Informational</td>
                            <td>Reconnaissance such as Port Scanning</td>
                            <td>Portal only</td>
                            <td>-</td>
                          </tr>
                          </tbody>
                        </table>
                      </div>
                      <h4 className="lh-base lead fw-bold mb-2 text-uppercase">EXCLUSIONS</h4>
                      <p>Supplier will not be liable under the following conditions:</p>
                      <ol type="i">
                        <li>Where scheduled maintenance was being carried out;</li>
                        <li>Where there has been any act or omission of Customer (or its Representatives) in breach of
                          the Services Agreement;
                        </li>
                        <li>For any security breaches caused by any Customer changes of which Supplier was not made
                          aware;
                        </li>
                        <li>For any security breaches where Supplier takes an action requested by Customer which has not
                          been agreed or tested as part of creating the relevant Runbook;
                        </li>
                        <li>Where Threat Signatures were not available by the Vendors to allow Supplier to identify a
                          threat including but not limited to Zero-day Attacks and APTs.
                        </li>
                      </ol>
                    </li>
                  </ol>
                </li>
                <li id="outsourced_data_protection_officer_dpo">
                  <h3 className="lh-base lead fw-bold mb-2 text-uppercase">Outsourced Data Protection Officer (DPO)</h3>
                  <p>A managed service where Customer can purchase a number of days (smallest amount is 0.5 days) per
                    month for DPO services. Where Customer does not use the total amount of time in any given month,
                    that time may be carried over to the subsequent month (but not longer).</p>
                  <p>Supplier will provide virtual consultation to Customer, information, advice and other related
                    services, in accordance with the DPO Service Levels below, to ensure that Customer processes the
                    personal data of its staff, customers, service providers or any other individuals (also referred to
                    as data subjects) in compliance with Applicable Data Protection Laws and best practice.</p>
                  <ol type="A">
                    <li>
                      <h4 className="lh-base lead fw-bold mb-2 text-uppercase">Supplier Obligations</h4>
                      <p>Supplier will:</p>
                      <p>Act as the Data Protection Officer (DPO) for Customer in accordance with Applicable Data
                        Protection Laws;</p>
                      <p>Facilitate Customer compliance with the UK/EU GDPR and other applicable data protection
                        legislation by ensuring effective systems and controls are in place to enable Customer to comply
                        with their legal obligations;</p>
                      <p>Act as Customer&rsquo;s intermediary between relevant stakeholders, including supervisory
                        authorities, data subjects, and business units;</p>
                      <p>Report notifiable data breaches identified and notified to Supplier by Customer to the
                        Information Commissioner&rsquo;s Office (ICO) and any relevant supervisory authority at the end
                        of any statutorily required notice period where the requisite notice has not been sent earlier
                        either by Customer or Supplier at Customer&rsquo;s instruction; and</p>
                      <p>Inform and advise Customer&rsquo;s senior management (where appointed to do so) in accordance
                        with Supplier&rsquo;s position as DPO of Customer.</p>
                    </li>
                    <li>
                      <h4 className="lh-base lead fw-bold mb-2 text-uppercase">Customer Obligations</h4>
                      <p>Customer will ensure compliance with all Applicable Data Protection Laws and in particular
                        Customer will:</p>
                      <p>Report all notifiable and potential data breaches to Customer assigned DPO <a
                        href="mailto:dposupport@bulletproof.co.uk">dposupport@bulletproof.co.uk</a> as soon as Customer
                        becomes aware of the breach;</p>
                      <p>Submit details of data breach(es) to Supplier for reporting to the ICO and any relevant
                        supervisory authority without undue delay; and</p>
                      <p>Where Customer fails to comply with reporting obligations above, Supplier shall not be liable
                        and Customer will indemnify Supplier for any penalties imposed by the ICO, any relevant
                        supervisory authority or any third-party claims, because of failure and or delay in reporting
                        notifiable breaches.</p>
                    </li>
                    <li>
                      <h4 className="lh-base lead fw-bold mb-2 text-uppercase">DPO Service Levels</h4>
                      <p>Priority levels will be addressed in line with the following Service Levels.</p>
                      <div className="table-responsive">
                        <table className="table table-hover mb-2">
                          <thead>
                          <tr>
                            <th>Type</th>
                            <th>Response Time</th>
                          </tr>
                          </thead>
                          <tbody>
                          <tr>
                            <td>Critical</td>
                            <td>within 1 Hour</td>
                          </tr>
                          <tr>
                            <td>Urgent</td>
                            <td>within 4 Hours</td>
                          </tr>
                          <tr>
                            <td>Non-urgent</td>
                            <td>by the end of the Next Business Day</td>
                          </tr>
                          </tbody>
                        </table>
                      </div>
                      <p>All Service Levels apply only from 9:00am to 5:30pm GMT Monday to Friday excluding UK bank
                        holidays (&ldquo;Working Hours&rdquo;). All DPO Service requests must originate with an email
                        sent to the allocated DPO and copied to <a
                          href="mailto:dposupport@bulletproof.co.uk">dposupport@bulletproof.co.uk</a> and the subject
                        line must contain the priority in accordance with the following:</p>
                      <ol type="i">
                        <li>&ldquo;Critical&rdquo; a scenario which will have serious immediate impact on the protection
                          of personal data
                        </li>
                        <li>&ldquo;Urgent&rdquo; for advice on GDPR/data protection topics that are subject to time
                          constraints
                        </li>
                        <li>&ldquo;Non-urgent&rdquo;for advice and guidance on GDPR/data protection issues and longer
                          term projects that do affect Customer&rsquo;s operations.
                        </li>
                      </ol>
                    </li>
                  </ol>
                </li>
                <li id="penetration_testing">
                  <h3 className="lh-base lead fw-bold mb-2 text-uppercase">Penetration Testing</h3>
                  <p>Supplier will perform penetration testing that evaluates Customer systems to validate and exploit
                    known vulnerabilities by assessing critical external and/or internal assets and/or APIs and/or web
                    applications and /or mobile applications and/or cloud infrastructure and/or wireless infrastructure
                    using experienced penetration testers to determine if Customer&rsquo;s organisation is susceptible
                    to attacks. Supplier will provide a report in both online and downloadable versions within 5 working
                    days of completion of a test.</p>
                  <ol type="A">
                    <li>
                      <h4 className="lh-base lead fw-bold mb-2 text-uppercase">Definitions:</h4>
                      <p>&ldquo;Late Availability Test&rdquo; where Customer contacts Supplier to conduct Penetration
                        Tests with five working days or less notice.</p>
                      <p>&ldquo;Red Team Penetration Test&rdquo; means the onsite presence of Supplier who will test the
                        System as described in a scope Annex made by Supplier to Customer.</p>
                      <p>&ldquo;Test Start Time&rdquo; means the provisional or definitive date and time listed in the
                        Order Form (or otherwise later expressly agreed by the parties in writing) that determines when
                        the Services will commence.</p>
                    </li>
                    <li>
                      <h4 className="lh-base lead fw-bold mb-2 text-uppercase">Customer Obligations:</h4>
                      <p>To submit, by upload into the Defense.com platform (Penetration Testing dashboard), any
                        necessary further scope details at least five working days prior to the start of the Penetration
                        Tests for efficient scheduling of necessary resources and time.</p>
                      <p>Where Customer fails to submit the necessary scope details, Supplier shall reschedule the
                        Penetration Test and Customer shall be liable for any charges.</p>
                      <p>Customer and Supplier will agree dates promptly after the Commencement Date or as set forth in
                        the Order Form for Supplier to deliver the Services within 12 months of the execution of the
                        Order Form and, where Customer fails to agree dates for the Services through no fault of the
                        Supplier, Customer will forfeit their right to the Services for the relevant 12-month period
                        and, for the avoidance of doubt, no refund or waiver of Fees or related costs, all owed upon
                        execution of the Order Form, will be issued by Supplier.</p>
                      <p>Where Customer requests a Late Availability Test and fails to timely provide Supplier with the
                        necessary information to commence the Penetration Test, Supplier shall not be obliged to carry
                        out the relevant Services and Customer will not be entitled to any refunds or waiver of Fees or
                        related costs.</p>
                      <p>Customer acknowledges that the Service will be provided remotely unless explicitly requested
                        and agreed otherwise. If onsite access is required to facilitate testing, Supplier will provide
                        the option of customer present equipment (CPE) to facilitate remote testing from
                        Supplier&rsquo;s secure remote location. In person tests may be provided upon request by
                        Customer or Supplier, subject to approval by Supplier.</p>
                      <p>Customer acknowledges that a Penetration Test is a snapshot in time and that it is limited to
                        the actions set out on the Order Form (which actions may be agreed in an incorporated scope
                        Annex document).</p>
                      <p>Customer shall comply with any rules imposed by any third party whose content or services are
                        accessed via the Services.</p>
                      <p>Customer shall inform Supplier forthwith if any of the Services are subject to interference or
                        malfunction.</p>
                      <p>Customer, prior to Penetration Tests, must proactively and appropriately backup all critical
                        data from its Systems that will form part of the Penetration Tests.</p>
                      <p>Where Customer engages Supplier to provide a Red Team Penetration Test, Customer further
                        represents and warrants to Supplier that Customer: a) has the necessary authority to instruct
                        Supplier to provide the Red Team Penetration Test; and b) shall sign a letter of authority (duly
                        signed by an authorised member of the executive board or equivalent) in the eventuality that
                        Supplier requires it.</p>
                    </li>
                  </ol>
                </li>
                <li id="virtual_chief_information_security_officer_vciso">
                  <h3 className="lh-base lead fw-bold mb-2 text-uppercase">Virtual Chief Information Security Officer
                    (VCISO)</h3>
                  <p>Supplier will provide a remote managed service that includes an experienced Information Security
                    Consultant to build and implement information security strategy for Customers. The service may
                    require an initial health check to establish the current security posture of Customer&rsquo;s
                    organisation and enable Supplier&rsquo;s Consultant to build a strategy. This Service can also
                    provide support to manage existing security frameworks such as Cyber Essentials and ISO 27001.
                    On-site visits may be arranged, where agreed, with Customer in exceptional circumstances.</p>
                  <ol type="A">
                    <li>
                      <h4 className="lh-base lead fw-bold mb-2 text-uppercase">Supplier Obligations</h4>
                      <p>Supplier will provide regular updates to Customer where reasonably requested;</p>
                      <p>Supplier will provide regular (at least monthly, at Supplier&rsquo;s discretion) updates on the
                        progress of the implementation of the agreed security strategy;</p>
                      <p>Supplier will only amend any agreed strategy with the written agreement of Customer; and</p>
                      <p>Supplier will work with third party suppliers of Customer where reasonably requested (e.g.,
                        outsourced IT providers).</p>
                    </li>
                    <li>
                      <h4 className="lh-base lead fw-bold mb-2 text-uppercase">Customer Obligations</h4>
                      <p>Customer will notify Supplier&rsquo;s designated VCISO of changes to Customer&rsquo;s business
                        including, interpreted broadly:</p>
                      <ol type="i">
                        <li>Structural/organisation changes e.g., acquisitions, sales;</li>
                        <li>Critical role and responsibility changes;</li>
                        <li>Key Customer supplier changes that may impact on information security;</li>
                        <li>New Customer supplier onboarding that may impact information security;</li>
                        <li>New software/solutions/hardware/cloud services that are planned; and</li>
                        <li>Key personnel changes.</li>
                      </ol>
                      <p>Customer will notify the VCISO of any security incidents or data breaches of which it becomes
                        aware.</p>
                      <p>Customer will notify VCISO of any Customer regulatory, legislative and/or contractual
                        requirements.</p>
                      <p>Customer will, when raising a request for assistance from its VCISO, ensure that <a
                        href="mailto:vciso@bulletproof.co.uk">vciso@bulletproof.co.uk</a> is copied on all messages.</p>
                    </li>
                  </ol>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesAgreementServiceSpecific;